import { migrateProseMirrorJson } from '@/components/tiptap-editor/tiptap-migration';
import { copyNode, findFirstMatchingBlockInRange, isProseMirrorJsonContent } from '@/lib/zy-tiptap-utils';
import authService from '@/services/zybook-auth-service';
import { type Editor } from '@tiptap/core';
import { Fragment, type Node as PMNode } from '@tiptap/pm/model';
import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import { Extension, type Content, type SetContentOptions } from '@tiptap/react';
import { PROTECTED_NODE_BYPASS_META_KEY } from './protected-node-extension';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        zyCommand: {
            /**
             * When the selection is a node selection, replace it with a text selection
             * just before or after that node so the node is no longer selected.
             * @param beforeNode - If true (default), caret moves to before the node; if false, after the node.
             *
             */
            clearNodeSelection: (beforeNode?: boolean) => ReturnType;

            /**
             * Duplicate a node to the target position.
             * @param node - The node to duplicate.
             * @param targetPos - The position to insert the duplicate node at.
             */
            duplicateNode: (node: PMNode, targetPos: number) => ReturnType;

            /**
             * Scroll the view so the node at the given document position is visible.
             * @param pos - The position to scroll to.
             */
            scrollToPos: (pos: number) => ReturnType;

            /**
             * Finds the first descendant block node with the specified name within the block at `pos`,
             * and sets a collapsed selection at the start of its content (immediately inside the node).
             * The block at `pos` is determined by `doc.nodeAt(pos)`.
             */
            selectDescendantNodeContent: (nodeName: string, pos?: number) => ReturnType;

            /**
             * Set the content of the editor with a meta object.
             * @param content - The content to set.
             * @param options - The options for setting the content.
             * @param meta - The meta object to set.
             */
            setContentWithMeta: (content: Content | Fragment | Node, options?: SetContentOptions, meta?: Record<string, any>) => ReturnType;

            /**
             * Save a document snapshot version with author/tags/remark metadata.
             * @param name - Version name.
             * @param meta - Version metadata (tags, remark, releaseNumber). Author is resolved from auth unless
             *   `omitAuthor` is set, for snapshots whose author lives elsewhere (a work status row's user).
             * @param force - Save even when no changes were detected. Defaults to true.
             */
            zySaveVersion: (
                name: string,
                meta: {
                    tags?: string[];
                    remark?: string;
                    releaseNumber?: string | number;
                    maDocumentWorkStatusId?: number;
                    workStatusAction?: string;
                    omitAuthor?: boolean;
                },
                force?: boolean,
            ) => ReturnType;
        };
    }
}

/**
 * Extension to add generic custom commands for the editor.
 * Useful for adding custom commands that are not part of the core TipTap extensions.
 */
export const ZyCommand = Extension.create({
    name: 'zyCommand',

    addCommands() {
        return {
            clearNodeSelection:
                (beforeNode: boolean = true) =>
                ({ editor, commands }) => {
                    const selection = editor.state.selection;
                    if (selection instanceof NodeSelection) {
                        return commands.setTextSelection(beforeNode ? selection.from - 1 : selection.to + 1);
                    }
                    return true;
                },

            duplicateNode:
                (node: PMNode, targetPos: number) =>
                ({ commands }) => {
                    const nodeCopy = copyNode(node);
                    return commands.insertContentAt(targetPos, nodeCopy);
                },

            scrollToPos:
                (pos: number) =>
                ({ editor }: { editor: Editor }) => {
                    try {
                        const { state, view } = editor;
                        if (pos < 0 || pos > state.doc.content.size) {
                            return false;
                        }
                        const dom = view.nodeDOM(pos);
                        if (dom == null) {
                            return false;
                        }
                        const el = dom instanceof HTMLElement ? dom : (dom as Node).parentElement;
                        if (el == null) {
                            return false;
                        }
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        return true;
                    } catch {
                        return false;
                    }
                },

            selectDescendantNodeContent:
                (nodeName: string, pos?: number) =>
                ({ tr, editor }) => {
                    if (pos === undefined) {
                        const selection = editor.state.selection;
                        pos = selection.from;
                    }
                    const doc = tr.doc;
                    const docSize = doc.content.size;
                    if (pos < 0 || pos > docSize) {
                        return false;
                    }
                    const root = doc.nodeAt(pos);
                    if (!root?.isBlock) {
                        return false;
                    }
                    const innerFrom = pos + 1;
                    const innerTo = pos + root.nodeSize - 1;
                    if (innerFrom > innerTo) {
                        return false;
                    }
                    const found = findFirstMatchingBlockInRange(doc, innerFrom, innerTo, nodeName);
                    if (!found) {
                        return false;
                    }
                    if (found.node.nodeSize > 1) {
                        tr.setSelection(TextSelection.create(tr.doc, found.pos + 1));
                        return true;
                    }
                    return false;
                },

            setContentWithMeta:
                (content: Content | Fragment | Node, options?: SetContentOptions, meta?: Record<string, any>) =>
                ({ chain }) => {
                    const metaObj = meta ?? { [PROTECTED_NODE_BYPASS_META_KEY]: true };
                    const contentToSet = isProseMirrorJsonContent(content) ? migrateProseMirrorJson(content) : content;
                    const chainedCommands = chain();
                    if (!metaObj) {
                        return chainedCommands.setContent(contentToSet, options).run();
                    }
                    for (const key in metaObj) {
                        chainedCommands.setMeta(key, metaObj[key]);
                    }
                    return chainedCommands.setContent(contentToSet, options).run();
                },

            zySaveVersion:
                (name: string, meta, force = true) =>
                ({ commands }) => {
                    if (typeof commands.saveVersion !== 'function') {
                        return false;
                    }

                    const author = authService.getUserFullName() ?? 'Unknown';
                    if (!author) {
                        return false;
                    }

                    return commands.saveVersion(name, force, {
                        ...(meta.omitAuthor ? {} : { author }),
                        tags: meta.tags?.length ? meta.tags : ['manual'],
                        remark: meta.remark ?? '',
                        ...(meta.releaseNumber != null ? { releaseNumber: meta.releaseNumber } : {}),
                        ...(meta.maDocumentWorkStatusId != null ? { maDocumentWorkStatusId: meta.maDocumentWorkStatusId } : {}),
                        ...(meta.workStatusAction != null ? { workStatusAction: meta.workStatusAction } : {}),
                    });
                },
        };
    },
});
